import React from 'react';
import styled from 'styled-components';
import Title from '@/components/Title';
import Paragraph from '@/components/Paragraph';
import { graphql } from 'gatsby';

import Container from '@/components/Container';
import BlockWrapper from '@/components/BlockWrapper';
import SmallCard, { SmallCardData } from '@/components/SmallCard';
import { isDarkTheme } from '@/utils/StyleUtils';
import { mediaQuery } from '@/utils/helpers';
import Link, { LinkStyle, LinkType } from '@/components/Link';
import About, { AboutType } from '@/components/About';

const CardsWrapper = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2.4rem;
  padding: 0;
  list-style: none;
  margin-bottom: 4rem;
  ${mediaQuery.md(`
  margin-bottom: 6.4rem;
`)}
  > li {
    flex: 0 1 calc(1 / 2 * 100% - (1 - 1 / 2) * 2.4rem);

    ${mediaQuery.sm(`
      flex: 0 1 calc(1 / 3 * 100% - (1 - 1 / 3) * 2.4rem);
    `)}
    ${mediaQuery.md(`
      flex: 0 1 calc(1 / 4 * 100% - (1 - 1 / 4) * 2.4rem);
    `)}
    ${mediaQuery.lg(`
      flex: 0 1 calc(1 / 6 * 100% - (1 - 1 / 6) * 2.4rem);
    `)}
  }
`;

const BlockContent = styled.div`
  text-align: center;
  margin-bottom: 4.8rem;
  *:first-child {
    margin-top: 0;
  }
  *:last-child {
    margin-bottom: 0;
  }
  ${mediaQuery.md(`
  margin-bottom: 7.2rem;
`)}
`;

const CTAWrapper = styled.div`
  width: 100%;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;

  > * {
    margin: 1rem;
  }
`;

type Props = {
  id: string;
  title: string;
  subtitle: string;
  backgroundColor: string;
  cards: Array<SmallCardData>;
  primaryLink: LinkType;
  secondaryLink: LinkType;
  about: AboutType;
};
const SmallCardBlock: React.FC<Props> = ({
  id,
  backgroundColor,
  title,
  subtitle,
  cards,
  primaryLink,
  secondaryLink,
  about,
}) => {
  const dark = isDarkTheme(backgroundColor);

  return (
    <BlockWrapper id={id} color={backgroundColor}>
      <Container>
        <BlockContent>
          {about && <About data={about} dark={dark} />}

          <Title
            size='xl'
            level='h3'
            margin='0.8rem 0 1.6rem'
            lightColor={dark}
          >
            {title}
          </Title>
          {subtitle && (
            <Paragraph
              color={dark ? 'White' : 'GreyBlue'}
              margin='0 0 1.5rem'
              weight='semiBold'
            >
              {subtitle}
            </Paragraph>
          )}
        </BlockContent>
        <CardsWrapper>
          {React.Children.toArray(
            cards.map((card) => {
              return SmallCard({ ...card, dark: dark });
            }),
          )}
        </CardsWrapper>
        <CTAWrapper>
          <Link {...primaryLink} linkStyle={LinkStyle.PRIMARY} />
          <Link {...secondaryLink} linkStyle={LinkStyle.SECONDARY} />
        </CTAWrapper>
      </Container>
    </BlockWrapper>
  );
};

export default SmallCardBlock;
export const largeCardsBlockFragmentQuery = graphql`
  fragment SmallCardsBlockFragment on ContentfulSmallCardsBlock {
    id
    __typename
    about {
      ...AboutFragment
    }
    title
    subtitle
    backgroundColor
    cards: smallCards {
      ...SmallCardFragment
    }
    primaryLink {
      ...LinkFragment
    }
    secondaryLink {
      ...LinkFragment
    }
  }
`;
